import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export default function HomePage() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [idadeError, setIdadeError] = useState('');
  const [whatsappValue, setWhatsappValue] = useState('');
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const targetDate = new Date('2026-03-13T19:30:00-04:00');

    const calcTimeLeft = () => {
      const now = new Date();
      const diff = targetDate.getTime() - now.getTime();

      if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds };
    };

    setTimeLeft(calcTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calcTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const scrollToForm = () => {
    const formElement = document.getElementById('inscricao');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handlePlayVideo = (videoId: string) => {
    setActiveVideo(videoId);
  };

  const formatWhatsApp = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const handleWhatsAppChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWhatsappValue(formatWhatsApp(e.target.value));
  };

  const handleIdadeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    if (e.target.value === '') {
      setIdadeError('');
    } else if (isNaN(val) || val < 3) {
      setIdadeError('Idade mínima: 3 anos');
    } else if (val > 35) {
      setIdadeError('Idade máxima: 35 anos');
    } else {
      setIdadeError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const idadeVal = parseInt((form.elements.namedItem('idade') as HTMLInputElement).value, 10);
    if (isNaN(idadeVal) || idadeVal < 3 || idadeVal > 35) {
      setIdadeError('Por favor, insira uma idade entre 3 e 35 anos.');
      return;
    }

    setFormStatus('submitting');
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6k312632h7pkqqstvmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        navigate('/obrigado');
      } else {
        throw new Error('Erro ao enviar formulário');
      }
    } catch (error) {
      setFormStatus('error');
      setFormMessage('Erro ao enviar inscrição. Por favor, tente novamente.');
      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 5000);
    }
  };

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(
      '🌟 Acabei de me inscrever na Seletiva Four Models!\n\n' +
      '📍 Cidade: Sapezal - MT\n' +
      '📅 Data: 13/03 • Sexta-feira\n' +
      '🕒 Horário: 19h30\n' +
      '🏨 Local: Pelegrini Palace Hotel – Sapezal - MT\n\n' +
      'Inscrição 100% gratuita! Participe você também 👉 https://sapezal.fourmodelsseletivas.com.br'
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-black shadow-lg' : 'bg-black'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <img 
            src="https://static.readdy.ai/image/103ec5ada18b00c7d95516c6aa3b7243/3c6853a00769ef0b898cf7c7be2b9fe6.png" 
            alt="Four Models" 
            className="h-8 sm:h-10 md:h-12 object-contain"
          />
          <button 
            onClick={scrollToForm}
            className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-full font-bold text-xs sm:text-sm hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            INSCREVA-SE
          </button>
        </div>
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/b1e6f203c1c86f7f2e02323a22506a53.jpg" 
              alt="Fashion Background" 
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/65 to-black/85"></div>
          </div>

          <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto w-full pt-24 sm:pt-20 pb-12">
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-6 sm:mb-8">
              <span className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-bold tracking-wider whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-calendar-event-fill text-sm"></i>
                13/03 • 19h30
              </span>
              <span className="bg-white/10 backdrop-blur-sm text-white border border-white/20 px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-bold tracking-wider whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-map-pin-fill text-yellow-500 text-sm"></i>
                SAPEZAL - MT
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-3 sm:mb-5 leading-tight">
              Sua Carreira na Moda, TV e<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
                Redes Sociais Começa Aqui
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-2 sm:mb-3 font-light max-w-3xl mx-auto">
              Seletiva Oficial <strong className="text-white font-semibold">Four Models</strong> em <strong className="text-yellow-500 font-bold">Sapezal - MT</strong>
            </p>

            <p className="text-lg sm:text-xl md:text-2xl text-white mb-8 sm:mb-10 font-semibold max-w-2xl mx-auto leading-relaxed px-2">
              Descubra se você ou seu filho(a) tem perfil para moda, TV e internet.
            </p>

            <button 
              onClick={scrollToForm}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-8 sm:px-10 md:px-14 py-4 sm:py-5 rounded-full text-base sm:text-lg md:text-xl font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-2xl hover:shadow-yellow-500/40 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              QUERO PARTICIPAR
            </button>

            <p className="text-yellow-500/80 text-xs sm:text-sm mt-3 sm:mt-4 font-medium">
              Inscrição gratuita • Não precisa experiência
            </p>

            <div className="mt-10 sm:mt-12 flex flex-wrap items-center justify-center gap-6 sm:gap-8 md:gap-12 text-white px-4">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-black text-yellow-500">03–35</div>
                <div className="text-xs sm:text-sm text-gray-300 mt-1">Anos</div>
              </div>
              <div className="w-px h-10 sm:h-12 bg-gray-600"></div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-black text-yellow-500">100%</div>
                <div className="text-xs sm:text-sm text-gray-300 mt-1">Gratuito</div>
              </div>
              <div className="w-px h-10 sm:h-12 bg-gray-600"></div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-black text-yellow-500">0</div>
                <div className="text-xs sm:text-sm text-gray-300 mt-1">Experiência Necessária</div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-3xl text-yellow-500"></i>
          </div>
        </section>

        {/* Countdown Section */}
        <section className="py-10 sm:py-12 bg-gray-950">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div className="bg-black/80 border border-yellow-500/30 rounded-2xl px-5 sm:px-8 py-6 sm:py-8">
              <div className="mb-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <span className="text-red-500 text-lg animate-pulse">🔴</span>
                  <p className="text-white font-black text-base sm:text-lg">
                    Vagas restantes: <span className="text-red-400 text-xl sm:text-2xl">23</span>
                  </p>
                </div>

                <div className="flex justify-center gap-1 mb-2">
                  {Array.from({ length: 30 }).map((_, i) => (
                    <div 
                      key={i}
                      className={`w-2 h-5 sm:w-2.5 sm:h-6 rounded-sm transition-all duration-700 ${
                        i < 7 ? 'bg-green-500' : 'bg-white/10'
                      }`}
                    />
                  ))}
                </div>

                <p className="text-gray-400 text-xs text-center">
                  <span className="text-red-400 font-bold">23</span> de 30 vagas disponíveis
                </p>
              </div>

              <div className="flex items-center gap-3 mb-5 max-w-xs mx-auto">
                <div className="flex-1 h-px bg-yellow-500/20"></div>
                <i className="ri-time-fill text-yellow-500/60 text-sm"></i>
                <div className="flex-1 h-px bg-yellow-500/20"></div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <i className="ri-fire-fill text-red-400 text-lg animate-pulse"></i>
                <p className="text-white font-black text-base sm:text-lg">
                  Restam apenas <span className="text-red-400 text-xl sm:text-2xl">23</span> vagas para esta cidade.
                </p>
                <i className="ri-fire-fill text-red-400 text-lg animate-pulse"></i>
              </div>

              <p className="text-gray-400 text-xs font-semibold tracking-widest uppercase text-center mb-4">
                O evento começa em
              </p>

              <div className="flex items-center justify-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="flex flex-col items-center">
                    <div className="bg-black/60 border border-yellow-500/30 rounded-xl w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center">
                      <span className="text-xl sm:text-2xl font-black text-yellow-400 tabular-nums">
                        {String(timeLeft.days).padStart(2, '0')}
                      </span>
                    </div>
                    <span className="text-gray-500 text-[10px] font-semibold mt-1.5 tracking-wider uppercase">Dias</span>
                  </div>
                  <span className="text-yellow-500/60 text-xl sm:text-2xl font-black mb-4 select-none">:</span>
                </div>

                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="flex flex-col items-center">
                    <div className="bg-black/60 border border-yellow-500/30 rounded-xl w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center">
                      <span className="text-xl sm:text-2xl font-black text-yellow-400 tabular-nums">
                        {String(timeLeft.hours).padStart(2, '0')}
                      </span>
                    </div>
                    <span className="text-gray-500 text-[10px] font-semibold mt-1.5 tracking-wider uppercase">Horas</span>
                  </div>
                  <span className="text-yellow-500/60 text-xl sm:text-2xl font-black mb-4 select-none">:</span>
                </div>

                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="flex flex-col items-center">
                    <div className="bg-black/60 border border-yellow-500/30 rounded-xl w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center">
                      <span className="text-xl sm:text-2xl font-black text-yellow-400 tabular-nums">
                        {String(timeLeft.minutes).padStart(2, '0')}
                      </span>
                    </div>
                    <span className="text-gray-500 text-[10px] font-semibold mt-1.5 tracking-wider uppercase">Min</span>
                  </div>
                  <span className="text-yellow-500/60 text-xl sm:text-2xl font-black mb-4 select-none">:</span>
                </div>

                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="flex flex-col items-center">
                    <div className="bg-black/60 border border-yellow-500/30 rounded-xl w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center">
                      <span className="text-xl sm:text-2xl font-black text-yellow-400 tabular-nums">
                        {String(timeLeft.seconds).padStart(2, '0')}
                      </span>
                    </div>
                    <span className="text-gray-500 text-[10px] font-semibold mt-1.5 tracking-wider uppercase">Seg</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-10 sm:py-12 bg-gradient-to-r from-yellow-500 to-yellow-600 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-40 h-40 bg-black rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-60 h-60 bg-black rounded-full translate-x-1/3 translate-y-1/3"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
            <p className="text-black font-black text-xl sm:text-2xl md:text-3xl mb-2">
              Não precisa experiência.
            </p>
            <p className="text-black/80 text-sm sm:text-base md:text-lg mb-5 sm:mb-6 font-medium">
              Buscamos talentos naturais. Sua oportunidade começa em Sapezal - MT.
            </p>
            <button 
              onClick={scrollToForm}
              className="bg-black text-yellow-500 px-8 sm:px-10 py-3 sm:py-4 rounded-full text-sm sm:text-base font-bold hover:bg-gray-900 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              INSCREVA-SE AGORA
            </button>
          </div>
        </section>

        {/* Who Can Participate */}
        <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-black mb-3 px-4">
                <a href="#quem-pode">
                  Quem Pode <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Participar</span>
                </a>
              </h4>
              <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto">
                Aberto para todos os perfis. Sem experiência necessária.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5 mb-8">
              {[
                { icon: 'ri-bear-smile-line', title: 'Crianças', age: '03 a 12 anos', desc: 'Início promissor na carreira' },
                { icon: 'ri-user-smile-line', title: 'Adolescentes', age: '13 a 17 anos', desc: 'Fase ideal para começar' },
                { icon: 'ri-user-star-line', title: 'Jovens', age: '18 a 25 anos', desc: 'Auge para o mercado' },
                { icon: 'ri-user-heart-line', title: 'Adultos', age: '26 a 35 anos', desc: 'Maturidade e presença' },
                { icon: 'ri-camera-line', title: 'Influencers', age: 'Todas as idades', desc: 'Potencial digital' }
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-5 sm:p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-500 text-center group cursor-pointer">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${item.icon} text-xl sm:text-2xl text-black`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-black mb-1">{item.title}</h3>
                  <p className="text-sm text-yellow-600 font-semibold mb-1">{item.age}</p>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-black rounded-2xl p-5 sm:p-7 text-center">
              <p className="text-lg sm:text-xl text-white font-bold">
                <span className="text-yellow-500">Zero experiência?</span> Sem problema. Avaliamos seu potencial natural.
              </p>
            </div>
          </div>
        </section>

        {/* Why Participate */}
        <section className="py-16 sm:py-20 bg-black">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-3 px-4">
                <a href="#beneficios">
                  Por Que <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Participar</span>
                </a>
              </h4>
              <p className="text-base sm:text-lg text-gray-400 max-w-xl mx-auto">
                Mais do que uma seletiva. Uma porta para o mercado.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              {[
                { icon: 'ri-user-search-line', title: 'Avaliação Profissional', desc: 'Feedback de especialistas sobre seu potencial real no mercado.' },
                { icon: 'ri-star-line', title: 'Oportunidade Real', desc: 'Acesso direto a trabalhos em moda, publicidade e entretenimento.' },
                { icon: 'ri-building-line', title: 'Ambiente Profissional', desc: 'Seletiva real com estrutura completa e equipe qualificada.' },
                { icon: 'ri-rocket-line', title: 'Desenvolvimento Artístico', desc: 'Orientação profissional e networking para sua carreira.' }
              ].map((item, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-6 sm:p-8 border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300 group cursor-pointer">
                  <div className="flex items-start gap-4 sm:gap-5">
                    <div className="w-12 h-12 sm:w-13 sm:h-13 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${item.icon} text-xl sm:text-2xl text-black`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5">{item.title}</h3>
                      <p className="text-gray-400 text-sm sm:text-base leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Event Information */}
        <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-black mb-3 px-4">
                <a href="#evento">
                  Informações do <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Evento</span>
                </a>
              </h4>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8">
              {[
                { icon: 'ri-calendar-event-fill', label: 'Data e Horário', value: '13/03', extra: '19h30' },
                { icon: 'ri-map-pin-2-fill', label: 'Local', value: 'Pelegrini Palace Hotel', extra: 'Sapezal - MT' },
                { icon: 'ri-group-fill', label: 'Formato', value: 'Presencial', extra: '' },
                { icon: 'ri-alarm-warning-fill', label: 'Vagas', value: 'Limitadas', extra: 'Por horário' }
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-5 sm:p-7 shadow-md text-center border border-gray-100 hover:border-yellow-500 transition-all duration-300 cursor-pointer">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className={`${item.icon} text-xl sm:text-2xl text-black`}></i>
                  </div>
                  <p className="text-xs sm:text-sm font-bold text-gray-500 mb-1">{item.label}</p>
                  <p className="text-lg sm:text-xl font-black text-black">{item.value}</p>
                  {item.extra && <p className="text-base sm:text-lg font-bold text-yellow-600">{item.extra}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Authority Section */}
        <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-yellow-500/5 rounded-full blur-3xl pointer-events-none"></div>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="text-center mb-10 sm:mb-14">
              <p className="text-xs sm:text-sm font-bold text-yellow-600 tracking-widest uppercase mb-3">
                Tradição · Credibilidade · Resultados
              </p>
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-black mb-3 px-4">
                <a href="#autoridade">
                  Quem Está Por Trás da <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Four Models?</span>
                </a>
              </h4>
              <p className="text-base sm:text-lg text-gray-500 max-w-2xl mx-auto">
                Mais de três décadas transformando vidas e lançando carreiras no mercado da moda e do entretenimento.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-10 sm:mb-14">
              {[
                { icon: 'ri-trophy-fill', value: '38+', label: 'Anos de mercado' },
                { icon: 'ri-global-fill', value: '100+', label: 'Modelos no mercado internacional' },
                { icon: 'ri-star-fill', value: '1ª', label: 'Top model brasileira lançada' },
                { icon: 'ri-film-fill', value: 'TV & Moda', label: 'Atores e modelos nas maiores emissoras' }
              ].map((item, index) => (
                <div key={index} className="bg-black rounded-2xl p-5 sm:p-7 text-center border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300 cursor-pointer group">
                  <div className="w-11 h-11 sm:w-13 sm:h-13 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${item.icon} text-lg sm:text-xl text-black`}></i>
                  </div>
                  <p className="text-2xl sm:text-3xl font-black text-yellow-500 mb-1">{item.value}</p>
                  <p className="text-gray-400 text-xs sm:text-sm">{item.label}</p>
                </div>
              ))}
            </div>

            <div className="bg-black rounded-2xl p-6 sm:p-8 md:p-10 border border-yellow-500/20 mb-8 sm:mb-10">
              <div className="space-y-4 sm:space-y-5">
                {[
                  { icon: 'ri-award-fill', text: 'Dirigida por Marla Drebes, referência nacional e internacional na descoberta de talentos.' },
                  { icon: 'ri-book-fill', text: 'Marla Drebes está no RankBrasil em 2004, é a Primeira brasileira a descobrir e projetar uma top model made in Brasil: Shirley Mallmann foi lançada em janeiro de 1995.' },
                  { icon: 'ri-user-star-fill', text: 'Responsável por lançar Shirley Mallmann — a primeira top model brasileira no cenário internacional.' },
                  { icon: 'ri-flashlight-fill', text: 'Revelou talentos como Jeisa Chiminazzo e diversos nomes que atuam no Brasil e no exterior.' },
                  { icon: 'ri-movie-2-fill', text: 'Trabalha também com atores, conectando talentos a testes nas maiores emissoras do país.' },
                  { icon: 'ri-medal-fill', text: 'Histórico de grandes projetos e recordes nacionais no segmento da moda.' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 sm:gap-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-base sm:text-lg text-black`}></i>
                    </div>
                    <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center">
              <button 
                type="button"
                onClick={scrollToForm}
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-xl hover:shadow-yellow-500/30 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                GARANTIR MINHA VAGA
              </button>
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="py-16 sm:py-20 bg-black relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img 
              src="https://barradobugres.fourmodelsseletivas.com.br/static/media/bg-testimonials.jpg" 
              alt="Background" 
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="text-center mb-10 sm:mb-14">
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-3 px-4">
                <a href="#historias">
                  Histórias de <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Sucesso</span>
                </a>
              </h4>
              <p className="text-base sm:text-lg text-gray-400 max-w-xl mx-auto">
                Assista os depoimentos de quem já passou pelas nossas seletivas.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 mb-8 sm:mb-10">
              {[
                { id: 'fWZeK74x6Us', name: 'Taís Pinheiro', location: 'Sinop - MT • Modelo Internacional', initial: 'T' },
                { id: 'GEI7zFUsukk', name: 'Beatriz Zachert', location: 'Sorriso - MT • Modelo Internacional', initial: 'B' },
                { id: 'Xvk5lA_x5ic', name: 'Douglas Malmman', location: 'CEO da Mädchen Models • Modelo Internacional', initial: 'D' },
                { id: 'zLSADgvoVXI', name: 'Valentina Marques', location: 'Presidente Figueiredo - AM • Atriz', initial: 'V' }
              ].map((video, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-yellow-500/20 flex flex-col">
                  <div className="relative w-full aspect-video bg-black">
                    {activeVideo === video.id ? (
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
                        title={`Depoimento de ${video.name}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div
                        className="absolute inset-0 w-full h-full cursor-pointer group"
                        onClick={() => handlePlayVideo(video.id)}
                      >
                        <img
                          src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
                          alt={`Depoimento de ${video.name}`}
                          className="w-full h-full object-cover object-top"
                        />
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-yellow-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-yellow-500/30">
                            <i className="ri-play-fill text-black text-2xl sm:text-3xl ml-1"></i>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-5 sm:p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-base sm:text-lg text-yellow-500"></i>
                      ))}
                    </div>

                    <div className="flex items-center gap-3 mt-auto">
                      <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-black font-bold text-base">{video.initial}</span>
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">{video.name}</p>
                        <p className="text-gray-500 text-xs">{video.location}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
              {[
                { value: '+50.000', label: 'Participantes em seletivas' },
                { value: '+1.000', label: 'Talentos descobertos' },
                { value: 'Brasil', label: 'Inteiro atendido' }
              ].map((stat, index) => (
                <div key={index} className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5 text-center">
                  <p className="text-2xl sm:text-3xl font-black text-yellow-500 mb-1">{stat.value}</p>
                  <p className="text-gray-400 text-xs sm:text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Special Guest Section */}
        <section className="py-20 sm:py-28 relative overflow-hidden bg-gradient-to-br from-gray-950 via-[#0a0a1a] to-gray-950">
          <div className="absolute top-0 left-0 w-72 h-72 bg-yellow-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-yellow-500/3 rounded-full blur-[120px]"></div>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="text-center mb-12 sm:mb-16">
              <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-5 py-2 mb-6">
                <i className="ri-star-fill text-yellow-500 text-sm"></i>
                <span className="text-yellow-500 text-xs font-semibold tracking-widest uppercase">
                  Participação Especial Confirmada
                </span>
                <i className="ri-star-fill text-yellow-500 text-sm"></i>
              </div>
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-white">
                <a href="#participacao-especial">
                  Etapa <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">Final</span>
                </a>
              </h4>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className="flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 rounded-3xl blur-2xl"></div>
                  <div className="relative w-[320px] h-[420px] sm:w-[380px] sm:h-[480px] rounded-2xl overflow-hidden shadow-2xl shadow-black/60 border border-yellow-500/20">
                    <img 
                      src="https://static.readdy.ai/image/103ec5ada18b00c7d95516c6aa3b7243/bcdca41a21e27164614ffcdeaee4e2ad.jpeg"
                      alt="Léo Belmonte - Ator brasileiro conhecido por Carrossel SBT - Barra do Bugres MT"
                      title="Léo Belmonte ator participação especial Four Models Barra do Bugres"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent h-32"></div>
                  </div>
                  <div className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/30 rotate-6">
                    <i className="ri-clapperboard-fill text-black text-xl"></i>
                  </div>
                </div>
              </div>

              <div className="text-center lg:text-left">
                <div className="flex items-center gap-2 justify-center lg:justify-start mb-2">
                  <div className="w-8 h-[2px] bg-yellow-500"></div>
                  <span className="text-yellow-500 text-xs font-semibold tracking-widest uppercase">Convidado Especial</span>
                </div>

                <h3 className="text-3xl sm:text-4xl md:text-5xl font-black mb-2">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600">
                    Léo Belmonte
                  </span>
                </h3>

                <p className="text-gray-400 text-sm sm:text-base font-medium mb-8 flex items-center justify-center lg:justify-start gap-2">
                  <i className="ri-film-fill text-yellow-500/70 text-base"></i>
                  Ator • Carrossel (SBT) • 3% (Netflix)
                </p>

                <div className="space-y-5 mb-10">
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    Na etapa final da <strong className="text-white">Convenção Four Models</strong>, contaremos com a participação especial do ator <strong className="text-white">Léo Belmonte</strong>, conhecido por interpretar <strong className="text-white">Jorge na novela Carrossel (SBT)</strong>, além de participação na série <strong className="text-white">3% da Netflix</strong> e outros trabalhos no audiovisual brasileiro.
                  </p>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    Sua presença reforça o compromisso da <strong className="text-white">Four Models</strong> em proporcionar <strong className="text-yellow-500">experiência real de mercado</strong>, conexão com profissionais atuantes e vivência prática no cenário artístico.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { icon: 'ri-tv-fill', title: 'Carrossel', subtitle: 'SBT' },
                    { icon: 'ri-netflix-fill', title: '3%', subtitle: 'Netflix' },
                    { icon: 'ri-movie-2-fill', title: 'Audiovisual', subtitle: 'Brasileiro' }
                  ].map((item, index) => (
                    <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                      <div className="w-9 h-9 bg-yellow-500/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                        <i className={`${item.icon} text-yellow-500 text-base`}></i>
                      </div>
                      <p className="text-white text-xs font-semibold">{item.title}</p>
                      <p className="text-gray-500 text-[11px]">{item.subtitle}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Registration Form */}
        <section id="inscricao" className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-10">
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-black mb-3 px-4">
                <a href="#inscricao">
                  Garanta sua <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">vaga gratuita</span>
                </a>
              </h4>
              <p className="text-sm sm:text-base text-gray-500">
                Leva menos de 30 segundos para se inscrever.
              </p>
            </div>

            {formStatus === 'success' ? (
              <div className="bg-white rounded-2xl shadow-2xl p-7 sm:p-10 border-2 border-yellow-400 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-check-line text-black text-3xl font-black"></i>
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-black mb-2">Inscrição Confirmada!</h3>
                <p className="text-gray-500 text-sm sm:text-base mb-6">
                  Entraremos em contato pelo WhatsApp com os detalhes.
                </p>

                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5 text-left mb-6">
                  <p className="text-black font-bold text-sm mb-3 flex items-center gap-2">
                    <i className="ri-map-pin-fill text-red-500 text-base"></i>
                    Nos vemos em Sapezal - MT!
                  </p>
                  <p className="text-gray-700 text-sm mb-1.5">
                    <strong>Data:</strong> 13/03 • Sexta-feira
                  </p>
                  <p className="text-gray-700 text-sm mb-1.5">
                    <strong>Horário:</strong> 19h30
                  </p>
                  <p className="text-sm">
                    <strong className="text-gray-700">Local:</strong>{' '}
                    <span className="text-yellow-600 font-semibold">Pelegrini Palace Hotel</span>
                  </p>
                </div>

                <p className="text-gray-500 text-xs sm:text-sm mb-3">
                  Chame seus amigos para participar também! 🎉
                </p>

                <button
                  type="button"
                  onClick={handleWhatsAppShare}
                  className="w-full bg-green-500 hover:bg-green-600 text-white px-6 py-4 rounded-full text-sm sm:text-base font-bold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap mb-4"
                >
                  <i className="ri-whatsapp-fill text-xl"></i>
                  Compartilhar no WhatsApp
                </button>

                <button
                  type="button"
                  onClick={() => { setFormStatus('idle'); setFormMessage(''); }}
                  className="text-yellow-600 hover:text-yellow-700 text-sm font-semibold underline cursor-pointer transition-colors"
                >
                  Fazer nova inscrição
                </button>
              </div>
            ) : (
              <form 
                id="form-inscricao" 
                data-readdy-form="true"
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl shadow-2xl p-7 sm:p-10 border-2 border-gray-100"
              >
                <div className="space-y-5 sm:space-y-6">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-bold text-gray-700 mb-2">
                      Nome Completo <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="nome"
                      name="nome"
                      type="text"
                      required
                      className="w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 border-gray-200 focus:border-yellow-500 placeholder-gray-400"
                      placeholder="Seu nome completo"
                    />
                  </div>

                  <div>
                    <label htmlFor="idade" className="block text-sm font-bold text-gray-700 mb-2">
                      Idade <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="idade"
                      name="idade"
                      type="number"
                      required
                      min="3"
                      max="35"
                      inputMode="numeric"
                      onChange={handleIdadeChange}
                      className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 placeholder-gray-400 ${idadeError ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'}`}
                      placeholder="Sua idade (entre 3 a 35 anos)"
                    />
                    {idadeError && (
                      <p className="mt-1.5 text-xs text-red-500 font-semibold flex items-center gap-1">
                        <i className="ri-error-warning-fill text-sm"></i>
                        {idadeError}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="whatsapp" className="block text-sm font-bold text-gray-700 mb-2">
                      WhatsApp <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="whatsapp"
                      name="whatsapp"
                      type="tel"
                      required
                      value={whatsappValue}
                      onChange={handleWhatsAppChange}
                      className="w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 border-gray-200 focus:border-yellow-500 placeholder-gray-400"
                      placeholder="(51) 98129-9804"
                    />
                  </div>

                  <div>
                    <label htmlFor="cidade" className="block text-sm font-bold text-gray-700 mb-2">
                      Cidade <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="cidade"
                      name="cidade"
                      type="text"
                      required
                      className="w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 border-gray-200 focus:border-yellow-500 placeholder-gray-400"
                      placeholder="Ex: Sapezal - MT"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full mt-8 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-6 py-4 sm:py-5 rounded-full text-sm sm:text-base font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer tracking-wide flex items-center justify-center text-center"
                >
                  {formStatus === 'submitting' ? 'ENVIANDO...' : 'GARANTIR MINHA VAGA GRATUITA'}
                </button>

                {formMessage && (
                  <div className="mt-4 p-4 rounded-xl text-center text-sm font-semibold bg-red-50 text-red-700 border border-red-200">
                    {formMessage}
                  </div>
                )}

                <p className="text-center text-xs text-gray-400 mt-4">
                  Inscrição 100% gratuita • Seus dados estão seguros
                </p>
              </form>
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-black py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center justify-center gap-5 sm:gap-6 mb-6 sm:mb-8">
            <img 
              src="https://static.readdy.ai/image/103ec5ada18b00c7d95516c6aa3b7243/f6ba88513f846dcaf1ba402d9a75d603.png"
              alt="Four Models" 
              className="h-14 sm:h-18 md:h-20 object-contain"
            />

            <div className="text-center">
              <p className="text-white font-bold text-base sm:text-lg mb-1">Seletiva Presencial Four Models</p>
              <p className="text-gray-400 text-sm">13 de Março às 19h30</p>
              <p className="text-gray-400 text-sm">Pelegrini Palace Hotel</p>
              <p className="text-gray-400 text-sm">Sapezal – MT</p>
            </div>

            <div className="flex items-center gap-5 text-gray-400">
              <a 
                href="https://www.instagram.com/fourmodelsoficial/" 
                target="_blank" 
                rel="nofollow noopener noreferrer"
                className="hover:text-yellow-500 transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a 
                href="https://www.facebook.com/fourmodelsoficial" 
                target="_blank" 
                rel="nofollow noopener noreferrer"
                className="hover:text-yellow-500 transition-colors cursor-pointer"
              >
                <i className="ri-facebook-circle-line text-xl"></i>
              </a>
              <a 
                href="https://whatsa.me/5551998977297/?t=Tenho%20d%C3%BAvidas%20sobre%20a%20sele%C3%A7%C3%A3o%20de%20Sapezal%20-%20MT%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es!" 
                target="_blank" 
                rel="nofollow noopener noreferrer"
                className="hover:text-yellow-500 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
              </a>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-5 text-center">
            <p className="text-gray-500 text-xs">
              © 2025 Four Models. Todos os direitos reservados. • <a href="https://readdy.ai/?ref=logo" target="_blank" rel="nofollow noopener noreferrer" className="hover:text-yellow-500 transition-colors font-semibold cursor-pointer">Website Builder</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}